import React, { useState, useEffect } from "react";
import { fetchExams } from "../../../services/exams.util";
import IconDropdown from "../components/IconDropdown";

const SubjectForm = ({ initialData, currentExam, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    icon: '',
    color: '#4285F4',
    order: 0,
    isFree: false,
    exams: currentExam?._id ? [currentExam._id] : []
  });
  const [submitting, setSubmitting] = useState(false);
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const isEdit = !!initialData;

  // Function to generate URL-friendly slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
  };

  useEffect(() => {
    const loadExams = async () => {
      try {
        setLoadingExams(true);
        const examsData = await fetchExams();
        setExams(examsData);
      } catch (error) {
        console.error('Failed to load exams:', error);
      } finally {
        setLoadingExams(false);
      }
    };

    loadExams();
  }, []);

  useEffect(() => {
    if (initialData) {
      // For editing: use data from initialData
      const exams = initialData.exams 
        ? (Array.isArray(initialData.exams) 
            ? initialData.exams.map(e => e._id || e)
            : [initialData.exams])
        : [];
      
      setFormData({
        title: initialData.title || '',
        slug: initialData.slug || '',
        description: initialData.description || '',
        icon: initialData.icon || '',
        color: initialData.color || '#4285F4',
        order: initialData.order || 0,
        isFree: initialData.isFree ?? false,
        exams: exams.length > 0 ? exams : (currentExam?._id ? [currentExam._id] : [])
      });
    } else if (currentExam) {
      // For adding new: pre-populate exams from currentExam
      setFormData(prev => ({
        ...prev,
        exams: [currentExam._id]
      }));
    }
  }, [initialData, currentExam]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: generateSlug(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleIconChange = (iconValue) => {
    setFormData(prev => ({
      ...prev,
      icon: iconValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await onSubmit(formData, isEdit);
      if (result?.success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5">
        <div className="modal-header">
          <h4 className="modal-title">{isEdit ? 'Edit Subject' : 'Create New Subject'}</h4>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="content-form">
            {/* Exam Selection */}
            <div className="form-group">
              <label className="form-label">
                Link to Exam(s)
              </label>
              {loadingExams ? (
                <div className="form-input" style={{ color: '#999' }}>Loading exams...</div>
              ) : (
                <select 
                  name="exams" 
                  value={formData.exams[0] || ''}
                  onChange={(e) => {
                    const selectedExamId = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      exams: selectedExamId ? [selectedExamId] : []
                    }));
                  }}
                  className="form-input"
                >
                  <option value="">Select an exam (optional)</option>
                  {exams.map((exam) => (
                    <option key={exam._id} value={exam._id}>
                      {exam.title}
                    </option>
                  ))}
                </select>
              )}
              {formData.exams.length > 0 && (
                <small className="form-help-text" style={{ color: '#28a745', display: 'block', marginTop: '8px' }}>
                  <i className="icon-check" style={{ marginRight: '4px' }}></i>
                  Subject will be linked to {exams.find(e => e._id === formData.exams[0])?.title || 'selected exam'}
                  {!isEdit && (
                    <span className="ml-10 text-light-2">
                      (You can link to more exams with pricing after creation)
                    </span>
                  )}
                </small>
              )}
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Title *</label>
                <input 
                  type="text" 
                  name="title" 
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., General Awareness"
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group half">
                <label className="form-label">Slug *</label>
                <input 
                  type="text" 
                  name="slug" 
                  value={formData.slug}
                  placeholder="Auto-generated from title"
                  className="form-input"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
                <small className="form-help-text">Auto-generated from title for URL use</small>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea 
                name="description" 
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the subject"
                className="form-textarea"
                rows="3"
              ></textarea>
            </div>

            <div className="form-group">
              <label className="d-flex align-items-center" style={{ gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isFree"
                  checked={!!formData.isFree}
                  onChange={handleInputChange}
                />
                <span className="form-label mb-0">Free subject (accessible without exam purchase)</span>
              </label>
            </div>
            
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Icon</label>
                <IconDropdown 
                  value={formData.icon}
                  onChange={handleIconChange}
                  color={formData.color}
                />
              </div>
              
              <div className="form-group half">
                <label className="form-label">Color</label>
                <input 
                  type="color" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                  className="form-input color-input"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-primary bg-blue"
              >
                {submitting ? 'Saving...' : (isEdit ? 'Update Subject' : 'Create Subject')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubjectForm; 